import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function UpdateCoveringModal({ screen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    coveredPositionsCount: screen.coveredPositionsCount || 0,
    coveringType: screen.coveringType || '',
    coveringNotes: screen.coveringNotes || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (parseInt(formData.coveredPositionsCount) < 0) {
      setError('Covered positions cannot be negative');
      return;
    }

    if (parseInt(formData.coveredPositionsCount) > screen.totalPositions) {
      setError(`Cannot exceed total positions (${screen.totalPositions})`);
      return;
    }

    if (parseInt(formData.coveredPositionsCount) > 0 && !formData.coveringNotes.trim()) {
      setError('Please provide covering notes when positions are covered');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/silkscreens/${screen.id}/covering`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coveredPositionsCount: parseInt(formData.coveredPositionsCount),
          coveringType: formData.coveringType || null,
          coveringNotes: formData.coveringNotes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update covering status');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#FF9800',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          🔴 Update Covering Status
        </h2>

        <div style={{
          background: '#fff3e0',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.9rem',
          color: '#e65100'
        }}>
          <strong>{screen.screenCode}</strong> - {screen.currentCustomerName}
          <br />
          {screen.currentItemDescription}
          <br />
          <div style={{ marginTop: '8px', fontWeight: '600' }}>
            Total Positions: {screen.amountAcross} × {screen.amountAround} = {screen.totalPositions}
          </div>
        </div>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              Number of Covered Positions *
            </label>
            <input
              type="number"
              name="coveredPositionsCount"
              value={formData.coveredPositionsCount}
              onChange={handleChange}
              min="0"
              max={screen.totalPositions}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF9800'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
              Maximum: {screen.totalPositions} positions
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              Covering Type
            </label>
            <select
              name="coveringType"
              value={formData.coveringType}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF9800'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            >
              <option value="">Select type...</option>
              <option value="temporary">Temporary</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.95rem'
            }}>
              Covering Notes {parseInt(formData.coveredPositionsCount) > 0 && '*'}
            </label>
            <textarea
              name="coveringNotes"
              value={formData.coveringNotes}
              onChange={handleChange}
              placeholder="Describe which positions are covered and why (e.g., Top-left and bottom-right covered for special run)"
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF9800'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#e0e0e0')}
              onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: loading ? '#b0bec5' : '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#F57C00')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#FF9800')}
            >
              {loading ? 'Updating...' : 'Update Covering'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateCoveringModal;
